import { useEffect, useRef } from 'react';

import { GOOGLE_CLIENT_ID } from '@/lib/env';

/** Botón oficial de Google Identity Services (auth 007).
    Carga el script GSI bajo demanda y entrega el ID token via onCredential.
    Sin VITE_GOOGLE_CLIENT_ID configurado no renderiza nada (fail-soft, como
    el backend con GOOGLE_OAUTH_CLIENT_ID vacío). */

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: object) => void;
          renderButton: (el: HTMLElement, options: object) => void;
        };
      };
    };
  }
}

const GSI_SRC = 'https://accounts.google.com/gsi/client';

interface Props {
  onCredential: (idToken: string) => void;
  text?: 'signin_with' | 'signup_with' | 'continue_with';
  /** Separador que solo existe si el botón existe (evita "— o —" huérfano). */
  divider?: string;
}

function GoogleSignInButton({ onCredential, text = 'continue_with', divider }: Props) {
  const slot = useRef<HTMLDivElement>(null);
  // GIS registra el callback UNA sola vez (effect con []); el ref garantiza
  // que el clic siempre invoque el handler vigente (invite/consents al día).
  const handlerRef = useRef(onCredential);
  handlerRef.current = onCredential;

  useEffect(() => {
    if (!GOOGLE_CLIENT_ID || !slot.current) return;

    const render = () => {
      if (!window.google || !slot.current) return;
      window.google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: (response: { credential: string }) => handlerRef.current(response.credential),
      });
      window.google.accounts.id.renderButton(slot.current, {
        theme: 'outline',
        size: 'large',
        text,
        width: 280,
        locale: 'es',
      });
    };

    if (window.google) {
      render();
      return;
    }
    // cleanup: si el componente se desmonta antes de que cargue el script
    // (navegación rápida login↔registro), el handler no queda colgado
    let script = document.querySelector(`script[src="${GSI_SRC}"]`) as HTMLScriptElement | null;
    if (!script) {
      script = document.createElement('script');
      script.src = GSI_SRC;
      script.async = true;
      document.head.appendChild(script);
    }
    script.addEventListener('load', render, { once: true });
    return () => script?.removeEventListener('load', render);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!GOOGLE_CLIENT_ID) return null;
  return (
    <>
      {divider && (
        <div className="my-4 flex items-center gap-3">
          <div className="h-px flex-1 bg-border" />
          <span className="text-xs text-muted-foreground">{divider}</span>
          <div className="h-px flex-1 bg-border" />
        </div>
      )}
      <div ref={slot} className="flex justify-center" />
    </>
  );
}

export default GoogleSignInButton;
